export const novaProgressMessages = {
  waiting: 'NovaAI is on it',
  planning: 'Consulting NovaAI… a hawk’s focus with AI precision',
  searching: 'Searching… like a hawk, but friendlier',
  syncing: 'Syncing with Apex… Hawk eyes: activated',
};

export type ProgressCopyPhase = 'opening' | 'preparing' | 'searching' | 'syncing' | 'working';
export interface ProgressCopy {id: string; text: string; hawk?: boolean;}
const onIt = {id: 'on-it', text: novaProgressMessages.waiting};
const closer = {id: 'closer-look', text: 'Let me take a closer look.'};
const focus = {id: 'focused', text: 'Keeping the focus on your request.'};
const planningHawk = {id: 'planning-hawk', text: novaProgressMessages.planning, hawk: true};
export const progressCopyPools: Record<ProgressCopyPhase, readonly ProgressCopy[]> = {
  opening: [onIt, {id: 'take-a-look', text: 'Let me take a look.'},
    {id: 'work-through', text: 'I’ll work through this with you.'}, focus,
    planningHawk, closer, {id: 'question-look', text: 'Giving your question a closer look.'}],
  preparing: [closer, focus, {id: 'details', text: 'Working through the details.'}, planningHawk, onIt],
  searching: [{id: 'source-look', text: 'Looking through the available sources.'},
    {id: 'source-search', text: 'Looking for the right sources.'},
    {id: 'search-hawk', text: novaProgressMessages.searching, hawk: true}],
  syncing: [{id: 'apex-sync', text: 'Keeping things in sync with Apex.'},
    {id: 'apex-connect', text: 'Connecting with Apex.'},
    {id: 'sync-hawk', text: novaProgressMessages.syncing, hawk: true}],
  working: [onIt, closer, focus, {id: 'details', text: 'Working through the details.'}],
};
const personalityLabels = new Set(Object.values(progressCopyPools).flat().map(copy => copy.text));

/** Personality copy is transient presentation, never evidence of completed work. */
export function isProgressPersonality(label: string): boolean {
  return personalityLabels.has(label.trim());
}

const preparationLabels = new Set([
  'preparing your request', 'choosing the appropriate workflow', 'thinking',
  'thinking through your request', 'working out what you need',
  'clarifying your request', 'considering your question',
]);

export function isPriorityProgress(label: string): boolean {
  if (preparationLabels.has(label.trim().toLowerCase())) return false;
  return /\b(writing|composing|finishing|finalizing|cancell?ed|cancellation|reconnecting|retrying|failed|error|clarifying|confirmation|waiting|limit|ended|complete)\b/i.test(label)
    || /preparing (?:your |the )?(?:answer|response)/i.test(label);
}

/** Presentation copy only. Never turn a proposed step into a claim of tool execution. */
export function progressPersonality(label: string, planned: boolean): Exclude<ProgressCopyPhase, 'opening'> | null {
  if (isProgressPersonality(label) || isPriorityProgress(label)) return null;
  if (planned || preparationLabels.has(label.trim().toLowerCase()) || /^(thinking|planning)\b/i.test(label)) return 'preparing';
  if (/^(searching|finding|looking for|looking up|locating)\b/i.test(label)) return 'searching';
  if (/^(HTTP\b|syncing with Apex\b|connecting to Apex\b)/i.test(label)) return 'syncing';
  return 'working';
}
